import React, { useCallback, useMemo, useRef, useState } from 'react'
import { styles } from '../../../../../../styles/tailwind-config'
import { BarcodeIcon, CameraIcon, CheckIcon, ChevronDown, ChevronsDown, Minus, PlusIcon, SaveIcon, Trash2Icon, TrashIcon, XIcon } from 'lucide-react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../../../components/ui/table'
import { Button } from '../../../../../../components/ui/button'
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from '../../../../../../components/ui/carousel'
import { Tab, TabItem } from '../../../../../../components/ui/tab'
import BarcodeScanner from '../../../../../../components/portal/BarcodeScanner'
import { toast } from 'sonner'
import Checkbox from '../../../../../../components/ui/checkbox'
import { OTHER_PART, PHOTO, PHOTO_SOORT, USED_SPAREPART, useInterventionInput } from '../../../../../../context/InterventionInputContext'
import { useBoundStore } from '../../../../../../zustand'
import { Sheet, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from '../../../../../../components/ui/sheet'
import { Label } from '../../../../../../components/ui/label'
import { Combobox } from '../../../../../../components/ui/combobox'
import { Input } from '../../../../../../components/ui/input'
import lodash from 'lodash'
import { Separator } from '../../../../../../components/ui/separator'
import { AutosizeTextarea } from '../../../../../../components/ui/textarea-autosize'
import { Switch } from '../../../../../../components/ui/switch'
import { z } from 'zod'
import { format } from 'date-fns'
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader } from '../../../../../../components/ui/alert-dialog'
import { AlertDialogCancel, AlertDialogTitle } from '@radix-ui/react-alert-dialog'
import { Skeleton } from '../../../../../../components/ui/skeleton'
import { Trans, useLingui } from '@lingui/react/macro'
import { useAuth } from '../../../../../../context/AuthProvider'

type AddSparePartProps = {
  trigger: React.ReactNode;
  defaultSparepart?: Sparepart;
  defaultBarcode?: string;
  onAddPart?: () => void;
}
const AddSparePart = ({ trigger, defaultSparepart, defaultBarcode, onAddPart }: AddSparePartProps) => {
  const spareparts = useBoundStore((state) => state.spareparts)
  const parameters = useBoundStore((state) => state.parameters)

  const { intervention, other_used_parts, used_spareparts, setused_spareparts, setother_used_parts, photos, setphotos } = useInterventionInput()

  const [selected_images, setselected_images] = useState<number[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selected_brand, setselected_brand] = useState<string | undefined>(defaultSparepart?.m_sMERK);
  const [article_number, setarticle_number] = useState<string>(defaultBarcode ? defaultBarcode : (defaultSparepart?.m_sSKU || ''));
  const [description, setdescription] = useState<string>(defaultSparepart?.m_sOMSCHRIJVING || '');
  const [part_name, setpart_name] = useState<string>(defaultSparepart?.m_sPARTNAAM || '');
  const [amount, setamount] = useState<number>(defaultSparepart?.m_rAANTAL || 0);
  const [warranty, setwarranty] = useState(false);
  const [price, setprice] = useState(0);

  const [open, setOpen] = useState(false);
  const [localPhotos, setLocalPhotos] = useState<typeof photos>([])
  const fileId = React.useId();
  const initRef = React.useRef(false);

  const visiblePhotos = React.useMemo(
    () => localPhotos.map((p, idx) => ({ photo: p, idx })),
    [localPhotos]
  );

  function handleToggleImage(idx: number) {
    setselected_images((prev) => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  }

  function handleRemoveImages() {
    setLocalPhotos((prev) => prev.filter((_, idx) => !selected_images.includes(idx)));
    setselected_images([]);
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    const readAsDataURL = (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

    (async () => {
      const base64s = await Promise.all(files.map(readAsDataURL));
      const now = new Date();

      const batch: PHOTO[] = base64s.map((b64) => ({
        attestid: '',
        customerdocid: '',
        docid: '',
        interventionid: String(intervention?.m_iINTERVENTIESID ?? ''),
        photo: b64,
        machineid: '',
        soort: 'FOTOPART',
        timestamp: format(now, 'yyyyMMddHHmmss'),
        usedpartid: defaultSparepart?.m_iWISSELSTUKID || 0,
        otherusedpartid:
          (defaultSparepart?.m_iWISSELSTUKID || 0) === 0
            ? `${part_name}-${article_number}`
            : '',
      }));

      setLocalPhotos(prev => [...prev, ...batch]);
    })();
  }

  function commitLocalPhotos() {
    setphotos(prev => {
      const prevClean = (prev ?? []).filter(p => {
        if (p.soort !== 'FOTOPART') return true;
        return defaultSparepart
          ? p.usedpartid !== defaultSparepart.m_iWISSELSTUKID
          : p.otherusedpartid !== `${part_name}-${article_number}`;
      });

      const normalized = localPhotos.map(p =>
        (defaultSparepart?.m_iWISSELSTUKID || 0) > 0
          ? p
          : { ...p, otherusedpartid: `${part_name}-${article_number}` }
      );

      return [...prevClean, ...normalized];
    });
  }

  function handleAddPart() {
    onAddPart && onAddPart();
    if (defaultSparepart) {
      setused_spareparts([...used_spareparts || [], {
        partid: defaultSparepart.m_iWISSELSTUKID,
        interventionid: intervention?.m_iINTERVENTIESID || '',
        amount: amount,
        warranty: warranty ? '1' : '0',
        topprice: price,
        machineid: '',
        timestamp: format(new Date(), 'yyyyMMddHHmmss'),
        bom: 0
      }]);
      commitLocalPhotos();
      setOpen(false);
    }
  }

  function handleAddOtherPart() {
    onAddPart && onAddPart();
    if (!defaultSparepart) {
      setother_used_parts([...other_used_parts || [], {
        interventionid: intervention?.m_iINTERVENTIESID || '',
        brand: selected_brand || '',
        sku: article_number,
        partname: part_name,
        description: description,
        amount: amount,
        warranty: warranty ? '1' : '0',
        topprice: price,
        machineid: '',
        timestamp: format(new Date(), 'yyyyMMddHHmmss'),
        bom: 0
      }]);
      commitLocalPhotos();
      setOpen(false);
    }
  }

  function isComplete() {
    try {
      z.object({
        article_number: z.string().min(1),
        name: z.string().min(1),
        amount: z.number().min(1),
      }).parse({
        article_number: article_number,
        name: part_name,
        amount: amount,
      })
      return true
    } catch (e) {
      return false
    }
  }

  const brands = Array.isArray(spareparts) ? lodash.uniqWith(spareparts?.map((sp) => sp.m_sMERK).filter((b) => b.length > 0) || [], lodash.isEqual) : []
  const brandOptions = brands.map((b) => { return { label: b, value: b } })

  const disPartPrice = parameters?.find((p) => p.m_sCODE === 'DISPPARTPRICE')?.m_sVALUE || '0'

  function getFilteredFromGlobal(): PHOTO[] {
    const all = photos ?? [];
    return all.filter((p) => {
      if (p.soort !== 'FOTOPART') return false;
      return defaultSparepart
        ? p.usedpartid === defaultSparepart.m_iWISSELSTUKID
        : p.otherusedpartid === `${part_name}-${article_number}`;
    });
  }

  React.useEffect(() => {
    if (open && !initRef.current) {
      setLocalPhotos(getFilteredFromGlobal());
      setselected_images([]);
      initRef.current = true;
    }
    if (!open) {
      initRef.current = false;
      setLocalPhotos([]);
      setselected_images([]);
    }
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent className='w-full min-w-[90vw] flex flex-col overflow-auto max-h-dvh'>
        <SheetHeader>
          <SheetTitle><Trans>Voeg een nieuwe wisselstuk toe</Trans></SheetTitle>
        </SheetHeader>
        <div className='flex flex-col h-full gap-2 overflow-auto'>

          {defaultSparepart &&
            <div className='flex flex-col gap-2'>
              <div>
                <Label><Trans>Merk</Trans></Label>
                <Combobox selectedValue={selected_brand} onValueChange={({ value }) => { setselected_brand(value.toString()) }} options={brandOptions} placeholder='Selecteer een merk...' disabled={!!defaultSparepart} />
              </div>
              <div>
                <Label><Trans>Artikelnummer</Trans></Label>
                <Input value={article_number} onChange={(e) => { setarticle_number(e.currentTarget.value) }} disabled={!!defaultSparepart} />
              </div>
              <div>
                <Label><Trans>Wisselstuk naam</Trans></Label>
                <Input value={part_name} onChange={(e) => { setpart_name(e.currentTarget.value) }} disabled={!!defaultSparepart} />
              </div>
              <div>
                <Label><Trans>Wisselstuk omschrijving</Trans></Label>
                <AutosizeTextarea className='max-h-56' value={description} onChange={(e) => { setdescription(e.currentTarget.value) }} disabled={!!defaultSparepart} />
              </div>
              <div>
                <Label><Trans>Aantal (max {defaultSparepart?.m_rAANTAL || 0})</Trans></Label>
                <Input value={amount.toString()} onBlur={(e) => { if (Number(e.currentTarget.value) > defaultSparepart?.m_rAANTAL) { setamount(defaultSparepart.m_rAANTAL) } else { setamount(Number(e.currentTarget.value)) } }} onChange={(e) => { setamount(Number(e.currentTarget.value)) }} type='number' />
              </div>
            </div>
          }

          {!defaultSparepart &&
            <div className='flex flex-col gap-2'>
              <div>
                <Label><Trans>Merk</Trans></Label>
                <Input value={selected_brand} onChange={(e) => { setselected_brand(e.currentTarget.value) }} />
              </div>
              <div>
                <Label><Trans>Artikelnummer</Trans></Label>
                <Input value={article_number} onChange={(e) => { setarticle_number(e.currentTarget.value) }} />
              </div>
              <div>
                <Label><Trans>Wisselstuk naam</Trans></Label>
                <Input className='max-h-56' value={part_name} onChange={(e) => { setpart_name(e.currentTarget.value) }} />
              </div>
              <div>
                <Label><Trans>Omschrijving</Trans></Label>
                <AutosizeTextarea className='max-h-56' value={description} onChange={(e) => { setdescription(e.currentTarget.value) }} />
              </div>
              <div>
                <Label><Trans>Aantal</Trans></Label>
                <Input value={amount.toString()} onBlur={(e) => { if (Number(e.currentTarget.value) > 5) { setamount(5) } }} onChange={(e) => { setamount(Number(e.currentTarget.value)) }} type='number' />
              </div>
              {disPartPrice === '1' &&
                <>
                  <div>
                    <Label><Trans>Prijs</Trans></Label>
                    <Input value={price.toString()} onBlur={(e) => { if (Number(e.currentTarget.value) > 5) { setprice(5) } }} onChange={(e) => { setprice(Number(e.currentTarget.value)) }} type='number' />
                  </div>
                  <div className='flex items-center gap-1'>
                    <Switch checked={warranty} onCheckedChange={(v) => { setwarranty(v) }} id='warranty' />
                    <Label htmlFor='warranty'><Trans>Wisselstuk onder garantie</Trans></Label>
                  </div>
                </>
              }
            </div>
          }


          <Separator className='my-2' />
          <div className='flex flex-col gap-2'>
            <h4 className={`${styles.h4}`}><Trans>Foto's toevoegen</Trans></h4>

            <div className='relative flex items-center justify-between h-10 p-1 border rounded-full'>

              <input
                ref={fileInputRef}
                id={fileId}
                type='file'
                accept='image/*,image/heic,image/heif'
                multiple
                onChange={handleFileChange}
                className='absolute w-px h-px -m-px p-0 overflow-hidden border-0 clip-rect'
                onClick={(e) => { (e.currentTarget as HTMLInputElement).value = '' }}
              />

              <label
                htmlFor={fileId}
                className='flex items-center justify-center w-8 h-full text-blue-600 rounded-full aspect-square cursor-pointer'
                aria-label="Foto's kiezen of maken"
              >
                <CameraIcon className='w-4 h-4' />
              </label>

              <button
                onClick={handleRemoveImages}
                disabled={selected_images.length === 0}
                className='flex items-center justify-center w-8 h-full text-red-600 rounded-full aspect-square disabled:opacity-30'
              >
                <Trash2Icon className='w-4 h-4' />
              </button>
            </div>

            <div className='grid grid-cols-2 gap-4 mt-2 lg:grid-cols-4'>
              {visiblePhotos.map(({ photo, idx }) => (
                <button
                  key={`photo-${idx}`}
                  onClick={() => handleToggleImage(idx)}
                  className='relative w-full overflow-hidden rounded-md min-h-32 aspect-video bg-neutral-100'
                >
                  {selected_images.includes(idx) && (
                    <div className='absolute right-2 top-2 flex items-center justify-center w-6 h-6 text-white bg-blue-600 border-2 border-white rounded-full'>
                      <CheckIcon className='w-4 h-4' />
                    </div>
                  )}
                  <img src={photo.photo || ''} className='object-cover w-full h-full' alt='MountQ image' />
                </button>
              ))}
            </div>
          </div>

        </div>
        <SheetFooter>
          <SheetClose asChild>
            <Button variant='ghost'><Trans>Annuleren</Trans></Button>
          </SheetClose>
          <SheetClose asChild>
            {defaultSparepart
              ? <Button onClick={() => { handleAddPart() }} disabled={!isComplete()}><Trans>Bevestigen</Trans></Button>
              : <Button onClick={() => { handleAddOtherPart() }} disabled={!isComplete()}><Trans>Toevoegen</Trans></Button>
            }
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

const SearchSpareParts = () => {
  const spareparts = useBoundStore((state) => state.spareparts)

  const brands = Array.isArray(spareparts) && lodash.uniqWith(spareparts?.map((sp) => sp.m_sMERK).filter((b) => b.length > 0) || [], lodash.isEqual)
  const brandOptions = Array.isArray(brands) ? brands.map((b) => { return { label: b, value: b } }) : []

  const [open_sheet, setopen_sheet] = useState(false);

  const [selected_brand, setselected_brand] = useState<string | undefined>(undefined);
  const [article_number, setarticle_number] = useState<string>('');
  const [search_term, setsearch_term] = useState<string>('');

  const [spareparts_filtered, setspareparts_filtered] = useState<Sparepart[] | undefined>(undefined);

  const { i18n } = useLingui()
  const { auth } = useAuth()
  const dbid = auth?.data?.databases?.[0]?.dbid;
  const isFR = i18n?.locale?.toLowerCase().startsWith('fr');
  const swapOrder = useMemo(
    () => (dbid === 1 || dbid === 7) && isFR,
    [dbid, isFR]
  );

  const pickSparepartLabel = useCallback((sp?: Sparepart | undefined) => {
    const naam = sp?.m_sPARTNAAM?.trim();
    const oms = sp?.m_sOMSCHRIJVING?.trim();
    return swapOrder ? (oms || naam || '-') : (naam || oms || '-');
  }, [swapOrder]);

  function handleSearch() {
    let tmp_sp = spareparts || [] as Sparepart[];

    if (selected_brand) {
      tmp_sp = tmp_sp.filter((sp) => sp.m_sMERK === selected_brand)
    }

    if (article_number) {
      tmp_sp = tmp_sp.filter((sp) => sp.m_sSKU.toLowerCase().includes(article_number.toLowerCase()))
    }

    if (search_term) {
      const term = search_term.toLowerCase();
      tmp_sp = tmp_sp.filter(sp => {
        const displayText =
          sp.m_sOMSCHRIJVING?.trim() !== ''
            ? sp.m_sOMSCHRIJVING.toLowerCase()
            : sp.m_sPARTNAAM?.toLowerCase() || '';

        return displayText.includes(term);
      });
    }

    setspareparts_filtered(tmp_sp)
  }


  return (
    <Sheet open={open_sheet} onOpenChange={setopen_sheet}>
      <SheetTrigger asChild>
        <button className='flex items-center justify-center w-8 h-full text-green-600 rounded-full aspect-square'><PlusIcon className='w-4 h-4' /></button>
      </SheetTrigger>
      <SheetContent className='w-full min-w-[90vw] flex flex-col overflow-auto max-h-dvh'>
        <SheetHeader>
          <SheetTitle><Trans>Zoek een wisselstuk</Trans></SheetTitle>
        </SheetHeader>
        <div className='flex flex-col h-full gap-2 overflow-auto'>
          <div className='flex flex-col gap-2'>
            <div>
              <Label><Trans>Merk</Trans></Label>
              <Combobox selectedValue={selected_brand} onValueChange={({ value }) => { setselected_brand(value.toString()) }} options={brandOptions} placeholder='Selecteer een merk...' />
            </div>
            <div>
              <Label><Trans>Artikelnr</Trans></Label>
              <Input value={article_number} onChange={(e) => { setarticle_number(e.currentTarget.value) }} />
            </div>
            <div>
              <Label><Trans>Trefwoord</Trans></Label>
              <Input value={search_term} onChange={(e) => { setsearch_term(e.currentTarget.value) }} />
            </div>
            <Button onClick={() => { handleSearch() }} className='w-full' disabled={!spareparts || spareparts.length === 0}><Trans>Zoeken</Trans></Button>
          </div>
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='sm:p-2'></TableHead>
                  <TableHead className='sm:p-2'><Trans>Merk</Trans></TableHead>
                  <TableHead className='sm:p-2'><Trans>Artikelnr</Trans></TableHead>
                  <TableHead className='sm:p-2'><Trans>Beschrijving</Trans></TableHead>
                  <TableHead className='sm:p-2'><Trans>Op voorraad</Trans></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!spareparts_filtered || spareparts_filtered.length === 0) && <TableRow><TableCell colSpan={99}><Trans>Geen resultaten gevonden</Trans></TableCell></TableRow>}
                {spareparts_filtered && spareparts_filtered.map((part) => (
                  <TableRow key={`${part.m_iWISSELSTUKID}`}>
                    <TableCell className='sm:p-2'>
                      <AddSparePart
                        onAddPart={() => { setopen_sheet(false) }}
                        defaultSparepart={part}
                        trigger={<button className='flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground'><PlusIcon className='w-4 h-4' /></button>}
                      />
                    </TableCell>
                    <TableCell className='sm:p-2'>{part?.m_sMERK}</TableCell>
                    <TableCell className='sm:p-2'>{part?.m_sSKU}</TableCell>
                    <TableCell className='sm:p-2'>{pickSparepartLabel(part)}</TableCell>
                    <TableCell className='sm:p-2'>{part?.m_rAANTAL}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className='flex justify-center'>
            <AddSparePart
              trigger={<Button variant='outline'><PlusIcon className='w-4 h-4' /><Trans>Ander wisselstuk toevoegen</Trans></Button>}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

const Main = () => {
  const [open_barcode, setopen_barcode] = useState(false);

  const spareparts = useBoundStore((state) => state.spareparts)

  const { intervention, used_parts, used_spareparts, other_used_parts, setused_parts, setother_used_parts, setused_spareparts } = useInterventionInput()

  const [popup_new_scanned_part, setpopup_new_scanned_part] = useState(false);
  const [scanned_barcode, setscanned_barcode] = useState<string>('');

  const { i18n } = useLingui()
  const { auth } = useAuth()
  const dbid = auth?.data?.databases?.[0]?.dbid;
  const isFR = i18n?.locale?.toLowerCase().startsWith('fr');
  const swapOrder = useMemo(
    () => (dbid === 1 || dbid === 7) && isFR,
    [dbid, isFR]
  );

  const pickSparepartLabel = useCallback((sp?: Sparepart | undefined) => {
    const naam = sp?.m_sPARTNAAM?.trim();
    const oms = sp?.m_sOMSCHRIJVING?.trim();
    return swapOrder ? (oms || naam || '-') : (naam || oms || '-');
  }, [swapOrder]);

  function handleScanBarcode(barcode: string) {
    setopen_barcode(false);
    setscanned_barcode(barcode)

    const part = spareparts?.find((p) => p.m_sBARCODE.toLowerCase() === barcode.toLowerCase())
    if (part) {
      // Add part
      setpopup_new_scanned_part(false);
      let tmp_used_parts = [...used_spareparts || []];
      setused_spareparts([...tmp_used_parts,
      {
        partid: part.m_iWISSELSTUKID,
        interventionid: intervention?.m_iINTERVENTIESID || '',
        amount: part.m_rAANTAL,
        warranty: 1,
        topprice: part.m_cPRICE,
        bom: 0,
        machineid: '',
        timestamp: format(new Date(), "yyyyMMddHHmmss"),
        partname: part.m_sPARTNAAM
      }
      ])
      // go to sheet with default sparepart
    } else {
      // Part not found
      setpopup_new_scanned_part(true);
    }
  }

  function handleChangeAmountUsedSparepart(part: USED_SPAREPART, amount: number) {
    let tmp = [...used_spareparts || []];
    const existingPartIndex = tmp.findIndex((p) => p.partid === part.partid);
    if (existingPartIndex >= 0) {
      tmp[existingPartIndex].amount = amount.toString();
    }
    setused_spareparts(tmp)
  }

  function handleChangeAmountOtherUsedpart(part: OTHER_PART, amount: number) {
    let tmp = [...other_used_parts || []];
    const existingPartIndex = tmp.findIndex((p) => p.sku === part.sku);
    if (existingPartIndex >= 0) {
      tmp[existingPartIndex].amount = amount.toString();
    }
    setother_used_parts(tmp)
  }

  return (
    <>
      {popup_new_scanned_part &&
        <AlertDialog open={popup_new_scanned_part} onOpenChange={(o) => { if (!o) { setscanned_barcode('') } }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle><Trans>Wisselstuk niet gevonden</Trans></AlertDialogTitle>
              <AlertDialogDescription><Trans>Wilt u een andere wisselstuk toevoegen met deze barcode?</Trans></AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button onClick={() => { setpopup_new_scanned_part(false) }} variant='ghost'>
                <Trans>Nee</Trans>
              </Button>

              <AddSparePart
                onAddPart={() => { setpopup_new_scanned_part(false) }}
                trigger={<AlertDialogAction>Ja</AlertDialogAction>}
                defaultBarcode={scanned_barcode}
              />

            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      }

      <BarcodeScanner onScan={(v) => { handleScanBarcode(v) }} open={open_barcode} onOpenChange={(o) => { setopen_barcode(o) }} />

      <div className='flex items-center justify-between h-10 p-1 border rounded-full'>
        <button onClick={() => { setopen_barcode(true) }} className='flex items-center justify-center w-8 h-full text-blue-600 rounded-full aspect-square'><BarcodeIcon className='w-4 h-4' /></button>
        <div className='flex items-center'>
          <SearchSpareParts />
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            {/* <TableHead className='whitespace-nowrap'>T. Eenheid</TableHead> */}
            <TableHead><Trans>Artikelnr</Trans></TableHead>
            <TableHead><Trans>Beschrijving</Trans></TableHead>
            <TableHead><Trans>Aantal</Trans></TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {used_parts?.map((part, i) => {
            const sparepart = spareparts?.find((sparepart) => sparepart.m_iWISSELSTUKID === part.m_iWISSELSTUKID)

            return (
              <TableRow key={`used-part-${i}`}>
                {/* <TableCell></TableCell> */}
                <TableCell>{spareparts ? sparepart?.m_sSKU : <Skeleton className='w-20 h-6' />}</TableCell>
                <TableCell>{part?.m_sPARTNAME ? part.m_sPARTNAME : pickSparepartLabel(sparepart)}</TableCell>
                <TableCell><input disabled value={part.m_rAANTAL || '0'} type='number' className='w-16 p-2 text-lg border rounded-sm focus:outline-none disabled:opacity-50' /></TableCell>
                <TableCell>
                  <button onClick={() => { setused_parts([...used_parts.filter((_, index) => index !== i)]) }}><TrashIcon className='w-4 h-4 text-red-600' /></button>
                </TableCell>
              </TableRow>
            )
          })}

          {used_spareparts?.map((part, i) => {
            const sparepart = spareparts?.find((sparepart) => sparepart.m_iWISSELSTUKID.toString() === part.partid.toString())

            return (
              <TableRow key={`used-sparepart-${i}`}>
                {/* <TableCell></TableCell> */}
                <TableCell>{sparepart?.m_sSKU}</TableCell>
                <TableCell>{part?.partname ? part.partname : pickSparepartLabel(sparepart)}</TableCell>
                <TableCell><input onBlur={(e) => { if (Number(e.currentTarget.value) > (sparepart?.m_rAANTAL || 999)) { handleChangeAmountUsedSparepart(part, (sparepart?.m_rAANTAL || 999)) } }} onChange={(e) => { handleChangeAmountUsedSparepart(part, Number(e.currentTarget.value)) }} value={part.amount || '0'} type='number' className='w-16 p-2 text-lg border rounded-sm focus:outline-none' /> </TableCell>
                <TableCell>
                  <button onClick={() => { setused_spareparts([...used_spareparts.filter((_, index) => index !== i)]) }}><TrashIcon className='w-4 h-4 text-red-600' /></button>
                </TableCell>
              </TableRow>
            )
          })}

          {other_used_parts?.map((part, i) => {
            return (
              <TableRow key={`other-used-sparepart-${i}`}>
                {/* <TableCell></TableCell> */}
                <TableCell>{part?.sku}</TableCell>
                <TableCell>{part?.partname} {part?.description}</TableCell>
                <TableCell><input onChange={(e) => { handleChangeAmountOtherUsedpart(part, Number(e.currentTarget.value)) }} value={part.amount || '0'} type='number' className='w-16 p-2 text-lg border rounded-sm focus:outline-none' /></TableCell>
                <TableCell>
                  <button onClick={() => { setother_used_parts([...other_used_parts.filter((_, index) => index !== i)]) }}><TrashIcon className='w-4 h-4 text-red-600' /></button>
                </TableCell>
              </TableRow>
            )
          })}

        </TableBody>
      </Table>
    </>
  )
}

const BOM = () => {
  const { intervention, used_parts, setused_parts } = useInterventionInput()
  const [selected_part_id, setselected_part_id] = useState<number | undefined>(undefined);

  function getRemainingStock(initialStock: BOM[], usedStock: BOM[]): BOM[] {
    return initialStock
      .map(stockItem => {
        // Find matching item in the usedStock array by comparing m_iPROJECTSTOCKID (or any unique identifier)
        const usedItem = usedStock.find(item => item.m_iPROJECTSTOCKID === stockItem.m_iPROJECTSTOCKID);

        if (usedItem) {
          const remainingAmount = stockItem.m_rAANTAL - usedItem.m_rAANTAL;

          // If the remaining amount is greater than 0, update it in the stockItem
          if (remainingAmount > 0) {
            return { ...stockItem, m_rAANTAL: remainingAmount };
          }

          // If the used amount matches or exceeds the initial amount, filter this item out
          return null;
        }

        // If there is no matching used item, return the stock item as is
        return stockItem;
      })
      .filter(item => item !== null) as BOM[];  // Filter out null values
  }

  function handleAddPartToUsed(part?: BOM) {
    if (!part) return;

    let tmp_used_parts = used_parts || [];
    const existingPart = tmp_used_parts.find((p) => p.m_iPROJECTSTOCKID === part.m_iPROJECTSTOCKID);

    if (existingPart) {
      // If the part is already in the used list, update the quantity
      setused_parts(
        tmp_used_parts.map((p) =>
          p.m_iPROJECTSTOCKID === part.m_iPROJECTSTOCKID
            // ? { ...p, m_rAANTAL: p.m_rAANTAL + 1 } // Increment by 1 or adjust logic for any specific amount
            ? { ...p, m_rAANTAL: part.m_rAANTAL } // Add all
            : p
        )
      );
    } else {
      // If the part is not in the used list, add it
      setused_parts([...tmp_used_parts, { ...part }]);
    }

    setselected_part_id(undefined)
  }

  function handleAddAllPartsToUsed() {
    if (!intervention) { return }

    setused_parts(intervention.m_aBOM)
  }

  function handleRemoveUsedPart(part: BOM) {
    used_parts && setused_parts(used_parts.filter((p) => p.m_iPROJECTSTOCKID !== part.m_iPROJECTSTOCKID));
  }

  function handleChangeUsedPartAmount(part: BOM, amount: number) {
    if (!intervention) return;
    // Find the corresponding part in the initial stock to get the max available amount
    const initialPart = intervention.m_aBOM.find(p => p.m_iPROJECTSTOCKID === part.m_iPROJECTSTOCKID);

    if (!initialPart) return; // If no matching initial part found, exit

    const maxAmount = initialPart.m_rAANTAL; // Maximum amount is the initial stock amount

    // Ensure the amount is not greater than the initial stock
    const updatedAmount = Math.min(amount, maxAmount);

    used_parts && setused_parts(
      used_parts.map((p) =>
        p.m_iPROJECTSTOCKID === part.m_iPROJECTSTOCKID ? { ...p, m_rAANTAL: updatedAmount } : p
      )
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {/* <div className='flex justify-end'>
        <Button className='gap-2'><SaveIcon className='w-4 h-4'/>Opslaan</Button>
      </div> */}
      <div className='w-full overflow-auto border rounded-md'>
        <Table>
          <TableHeader><TableRow>
            <TableHead className='w-10'></TableHead>
            <TableHead><Trans>Beschrijving</Trans></TableHead>
            <TableHead className='w-20'><Trans>Aantal</Trans></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(intervention) && getRemainingStock(intervention.m_aBOM, (used_parts || [])).map((part, i) => {
              return (
                <TableRow onClick={() => { if (selected_part_id === part?.m_iPROJECTSTOCKID) { setselected_part_id(undefined) } else { setselected_part_id(part?.m_iPROJECTSTOCKID) } }} className={`${selected_part_id === part?.m_iPROJECTSTOCKID && '!bg-primary text-primary-foreground'} cursor-pointer`} key={i}>
                  <TableCell><Checkbox checked={selected_part_id === part?.m_iPROJECTSTOCKID} /></TableCell>
                  <TableCell>{part?.m_sPARTNAME}</TableCell>
                  <TableCell><p>{part?.m_rAANTAL || "0"}</p></TableCell>
                </TableRow>
              )
            })}

          </TableBody>
        </Table>
      </div>
      <div className='flex items-center justify-center gap-1'>
        <button onClick={() => { if (typeof (selected_part_id) === "number" && intervention) { handleAddPartToUsed(intervention.m_aBOM?.find(b => b?.m_iPROJECTSTOCKID === selected_part_id)) } }} disabled={selected_part_id === undefined} className='flex items-center justify-center w-8 h-8 border rounded-full disabled:opacity-50'><ChevronDown className='w-4 h-4' /></button>
        <button onClick={() => { handleAddAllPartsToUsed() }} className='flex items-center justify-center w-8 h-8 border rounded-full'><ChevronsDown className='w-4 h-4' /></button>
        {/* <button disabled={selected_part_id === undefined} className='flex items-center justify-center w-8 h-8 border rounded-full disabled:opacity-50'><Minus className='w-4 h-4'/></button> */}
      </div>
      <div className='w-full overflow-auto border rounded-md'>
        <Table>
          <TableHeader><TableRow>
            <TableHead><Trans>Beschrijving</Trans></TableHead>
            <TableHead className='w-20'><Trans>Aantal</Trans></TableHead>
            <TableHead className='w-10'></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {used_parts?.map((part) => {
              return (
                <TableRow key={`used-part-${part.m_iPROJECTSTOCKID}`}>
                  <TableCell>{part?.m_sPARTNAME}</TableCell>
                  <TableCell><input min={1} onChange={(e) => { handleChangeUsedPartAmount(part, Number(e.target.value)) }} value={part?.m_rAANTAL || "0"} className='w-16 p-2 text-lg border rounded-sm focus:outline-none' type="number" /></TableCell>
                  <TableCell>
                    <button className='text-red-600' onClick={() => { handleRemoveUsedPart(part) }}><TrashIcon className='w-4 h-4' /></button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

const SpareParts = () => {
  const [api, setApi] = React.useState<CarouselApi>()
  const [current, setCurrent] = React.useState(1)

  React.useEffect(() => {
    if (!api) {
      return
    }

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1)
    })
  }, [api])

  return (
    <div className='flex flex-col gap-4'>
      <h3 className={`${styles.h3}`}><Trans>Gebruikte wisselstukken</Trans></h3>
      <div>
        <Tab value={current.toString()} onValueChange={(v) => { api?.scrollTo(parseFloat(v) - 1) }}>
          <TabItem value='1'><Trans>Overzicht</Trans></TabItem>
          <TabItem value='2'><Trans>Bill of materials</Trans></TabItem>
        </Tab>
      </div>

      <div>
        <Carousel setApi={setApi} opts={{ loop: true }} className='flex-1'>
          <CarouselContent className='-ml-4'>
            <CarouselItem className='pl-4'>
              <Main />
            </CarouselItem>
            <CarouselItem className='pl-4'>
              <BOM />
            </CarouselItem>
          </CarouselContent>
        </Carousel>
      </div>
    </div>
  )
}

export default SpareParts